<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { CodesDocument, CodeType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BCollapse,
  BForm,
  BFormGroup,
  BFormInput,
  BPagination,
  BSpinner,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CodeList from '@/components/code/CodeList.vue';
import CodeGenerator from '@/components/code/CodeGenerator.vue';
import { deepParseDates } from '@/utils/date-utils';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';

// Filtering and paging state
const codePattern = ref('');
const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return codesQueryResult.value?.codes?.total || 0;
});

const showCodeGenerator = ref<boolean>(false);

const codes = computed<CodeType[]>(() => {
  return (
    codesQueryResult.value?.codes?.codes
      ?.filter((code) => code && code !== null)
      .map((code) => ({ ...(deepParseDates(code) as CodeType) })) || []
  );
});

// --- Query Codes with Kits, Coupons and Licenses ---
const {
  result: codesQueryResult,
  loading: codesQueryInProgress,
  error: codesQueryError,
  refetch: refetchCodes,
} = useQuery(CodesDocument, {
  acrossOrganizations: true,
  codePattern: codePattern.value,
  page: pagingPageCurrent.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

function requestRefetchCodes() {
  refetchCodes({
    acrossOrganizations: true,
    codePattern: codePattern.value,
    page: pagingPageCurrent.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
    })
    .then(() => {
      console.log('refetched');
    });
}

function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchCodes();
  }
}

function clearFilter() {
  codePattern.value = '';
  pagingPageCurrent.value = 1;

  requestRefetchCodes();
}

function codesGenerationSuccess() {
  requestRefetchCodes();
  showCodeGenerator.value = false;
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchCodes();
});
</script>

<template>
  <div>
    <h1><font-awesome-icon :icon="['fas', 'ticket']" /> Kits and coupons</h1>
    <hr />
    <p>
      Browse the kits and coupons identified by the associated code. Check codes
      association with organizations and inspect kit and coupon details.
    </p>
    <!-- Content -->
    <div>
      <!-- Code Generator -->
      <b-button
        @click="showCodeGenerator = !showCodeGenerator"
        variant="outline-dark"
        class="mb-2"
      >
        <font-awesome-icon :icon="['fas', 'ticket']" />
        Generate new codes</b-button
      >
      <b-collapse v-model="showCodeGenerator" id="code-generator">
        <CodeGenerator @codes-generated="codesGenerationSuccess" class="mt-3" />
      </b-collapse>

      <!-- Code List Overlay -->
      <h3 class="mt-4">List of existing codes</h3>
      <b-alert
        v-model="codesQueryError"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ codesQueryError }}
        <b-button
          @click="requestRefetchCodes"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Filter -->
      <div class="mt-3 mb-2">
        <BForm
          class="d-flex flex-row align-items-end flex-wrap gap-2"
          @submit.prevent="onSearchRequested"
        >
          <BFormGroup label="Code" label-for="code-pattern">
            <BFormInput
              id="code-pattern"
              type="text"
              v-model="codePattern"
              placeholder="e.g. X32D-C384"
            />
          </BFormGroup>
          <!-- Filter action buttons -->
          <BButton variant="outline-secondary" @click="clearFilter">
            <font-awesome-icon icon="trash" />
            Clear filter
          </BButton>
          <BButton
            variant="outline-dark"
            type="submit"
            :disabled="codesQueryInProgress"
          >
            <span v-if="codesQueryInProgress">
              <b-spinner small label=""></b-spinner>
              Searching
            </span>
            <span v-else>
              <font-awesome-icon icon="magnifying-glass" />
              Refresh
            </span>
          </BButton>
        </BForm>
      </div>

      <!-- Paging (above) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />

      <!-- List of Codes -->
      <CodeList :codes="codes" :loading="codesQueryInProgress" />

      <!-- Paging (below) -->
      <BPagination
        v-if="pagingTotalRows > pagingItemsPerPage"
        v-model="pagingPageCurrent"
        :total-rows="pagingTotalRows"
        :per-page="pagingItemsPerPage"
        pills
        size="sm"
      />
    </div>
  </div>
</template>

<style scoped></style>
